import * as THREE from 'three';
import type { Hero } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** How long a chat line stays over the speaker's head. */
const SAY_MS = 6000;

/**
 * A small name over each hero's miniature, above its health bar: yours highlighted, ghosts and
 * hired hands in a quieter tone. They shrink as the camera pulls back, and when two overlap the
 * nearer one wins, so a crowd stays readable on a phone. A chat line shows a few seconds in a bubble
 * over the name (a speaking hero's name is never the one hidden).
 */
export class NameTags {
  readonly el = h('div.name-tags');
  private tags = new Map<string, HTMLElement>();
  private said = new Map<string, { text: string; until: number }>();
  private v = new THREE.Vector3();

  /** Both are read when placing (the screens that own this aren't built yet when it is). */
  constructor(
    private me: () => string,
    private names: () => Record<string, string>,
  ) {}

  /** `pid` said `text`: over their head for a few seconds, if their hero is on the field (true then). */
  say(pid: string, text: string, heroes: Hero[]): boolean {
    if (!heroes.some((x) => x.pid === pid && x.downTicks <= 0)) return false;
    this.said.set(pid, { text, until: performance.now() + SAY_MS });
    return true;
  }

  private label(hero: Hero): string {
    if (hero.control === 'merc') return t('party.mercTag', { cls: t(`class.${hero.cls}`) });
    return this.names()[hero.pid] || hero.name || t(`class.${hero.cls}`);
  }

  place(heroes: Hero[], positions: Map<number, THREE.Vector3>, camera: THREE.Camera, w: number, ht: number): void {
    const seen = new Set<string>();
    const placed: { x: number; y: number; z: number; el: HTMLElement; wpx: number; talking: boolean; mine: boolean }[] = [];
    const now = performance.now();
    for (const hero of heroes) {
      const p = positions.get(hero.id);
      if (!p || hero.downTicks > 0) continue;
      seen.add(hero.pid);
      let el = this.tags.get(hero.pid);
      if (!el) {
        el = h(`span.name-tag${hero.pid === this.me() ? '.me' : ''}`, {}, h('span.say'), h('span.nm'));
        this.el.append(el);
        this.tags.set(hero.pid, el);
      }
      const text = this.label(hero);
      const nm = el.lastElementChild as HTMLElement;
      if (nm.textContent !== text) nm.textContent = text;
      const said = this.said.get(hero.pid);
      const talking = !!said && said.until > now;
      if (said && !talking) this.said.delete(hero.pid);
      const bubble = el.firstElementChild as HTMLElement;
      const line = talking ? said.text : '';
      if (bubble.textContent !== line) bubble.textContent = line;
      el.classList.toggle('talking', talking);
      el.classList.toggle('ghost', hero.control === 'ghost');
      el.classList.toggle('merc', hero.control === 'merc');
      this.v.copy(p).setY(p.y + 1.45).project(camera);
      if (this.v.z > 1) {
        setStyle(el, 'display', 'none');
        continue;
      }
      placed.push({ x: (this.v.x * 0.5 + 0.5) * w, y: (-this.v.y * 0.5 + 0.5) * ht, z: this.v.z, el, wpx: text.length * 7 + 10, talking, mine: hero.pid === this.me() });
    }
    // Yours first (never lost in a crowd), then who is talking, then nearer first; a tag that would sit on
    // top of one already shown is hidden.
    placed.sort((a, b) => Number(b.mine) - Number(a.mine) || Number(b.talking) - Number(a.talking) || a.z - b.z);
    const shown: { x: number; y: number; wpx: number }[] = [];
    for (const it of placed) {
      // Pulled back (z close to 1), names get smaller.
      const scale = Math.max(0.7, Math.min(1, (1 - it.z) * 40));
      const clash = shown.some((o) => Math.abs(o.x - it.x) < (o.wpx + it.wpx) * 0.5 * scale && Math.abs(o.y - it.y) < 16);
      if (clash) {
        setStyle(it.el, 'display', 'none');
        continue;
      }
      shown.push(it);
      setStyle(it.el, 'display', '');
      setStyle(it.el, 'transform', `translate(${it.x.toFixed(1)}px, ${it.y.toFixed(1)}px) translate(-50%, -100%) scale(${scale.toFixed(2)})`);
    }
    for (const [pid, el] of this.tags) {
      if (seen.has(pid)) continue;
      el.remove();
      this.tags.delete(pid);
      this.said.delete(pid);
    }
  }
}
